import React, { useCallback } from 'react'
import { Stack, Typography } from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { useTranslation } from 'react-i18next'

import { CORAL_600, GRAY_700, GREEN_600 } from 'constants/styling/theme'
import { useCreateDraft, useSaveDraft } from 'dataQueries/purchaseFlowDrafts.query'
import { logger } from 'utils/logging'
import { MUITooltip } from 'components/common/MUITooltip'
import { serializeDraft } from '../../store/serializeDraft'
import { usePfStore } from '../../store'

/**
 * Small save-status badge surfaced in the top bar of the Catalog step.
 *
 * States:
 *   - `idle`   → nothing rendered (no draft saved yet)
 *   - `saving` → check icon (grey) + "Saving…"
 *   - `saved`  → check icon (green) + "Saved" — A#7: persists once the order is
 *                saved so the user keeps a durable "your order is safe" cue
 *                (previously auto-hid after 3 s, which read as the badge
 *                disappearing).
 *   - `error`  → warning icon (coral) + "Not saved", clickable retry
 */
export const AutosaveIndicator: React.FC = () => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const saveStatus = usePfStore((s) => s.draft.saveStatus)

  const saveDraft = useSaveDraft()
  const createDraft = useCreateDraft()

  const handleRetry = useCallback(async () => {
    const state = usePfStore.getState()
    if (state.draft.isHydrating || state.draft.saveStatus === 'saving') return

    try {
      state.markSaving()

      let draftId = state.draft.draftId
      if (!draftId) {
        draftId = await createDraft.mutateAsync()
        usePfStore.getState().setDraftId(draftId)
      }

      const finalDto = serializeDraft(usePfStore.getState())
      await saveDraft.mutateAsync({ draftId, dto: finalDto })

      usePfStore.getState().markSaved()
    }
    catch (err) {
      logger.error('AutosaveIndicator retry failed', err, {
        context: { component: 'AutosaveIndicator', action: 'retry' },
      })
      usePfStore.getState().markError(
        err instanceof Error ? err.message : 'Retry failed'
      )
    }
  }, [createDraft, saveDraft])

  if (saveStatus === 'idle') return null

  if (saveStatus === 'error') {
    return (
      <MUITooltip content={t('purchase_flow_revamp:catalog.autosave_retry_tooltip')}>
        <Stack
          direction="row"
          alignItems="center"
          gap={0.6}
          onClick={handleRetry}
          data-cy="pf-revamp-autosave-error"
          sx={{ cursor: 'pointer' }}
        >

          <ErrorOutlineRoundedIcon
            sx={{ fontSize: '1.6rem', color: CORAL_600 }}
          />

          <Typography variant="text-sm" color={GRAY_700}>
            {t('purchase_flow_revamp:catalog.autosave_error')}
          </Typography>

        </Stack>
      </MUITooltip>
    )
  }

  const isSaving = saveStatus === 'saving'

  return (
    <Stack direction="row" alignItems="center" gap={0.6}>

      <CheckCircleOutlineRoundedIcon
        sx={{ fontSize: '1.6rem', color: isSaving ? GRAY_700 : GREEN_600 }}
      />

      <Typography variant="text-sm" color={GRAY_700}>
        {isSaving
          ? t('purchase_flow_revamp:catalog.autosave_saving')
          : t('purchase_flow_revamp:catalog.autosave_saved')}
      </Typography>

    </Stack>
  )
}
